import { useState } from 'react';
import { toast } from 'react-toastify';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';

import { useMutationAddPaymentMethod } from './useMutationAddPaymentMethod';
import { useMutationCreateSetupIntent } from './useMutationCreateSetupIntent';
import { useMutationSavePaymentInfo } from './useMutationSavePaymentInfo';

import { FEATURE_FLAGS, PAYMENT_METHOD } from '~common';
import { useLanguageContext } from '~providers';
import { checkEnabledFeatureFlag } from '~utils';

const UNKNOWN_ERROR_KEY = 'text.hooks.useEjectBattery.error';
const UNKNOWN_ERROR_FALLBACK =
  'An unknown error occurred when setup credit card intent!';

export const useAddCreditCard = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [errorOnAddCard, setErrorOnAddCard] = useState<string | null>(null);
  const { lt } = useLanguageContext();
  const stripe = useStripe();
  const elements = useElements();

  // Migrate the add-card tokenization to a server SetupIntent + confirmCardSetup
  // (ENG-7877). Gated behind the same flag as the wallet flow so both can be
  // rolled out / reverted together.
  const isMultiCardsPerUserEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.MULTI_CARDS_PER_USER_BY_WEB_PLATFORM,
  );

  const savePaymentInfoMutation = useMutationSavePaymentInfo({
    shouldToast: false,
    onErrorCallback: (errorMsg) => {
      setErrorOnAddCard(errorMsg);
    },
  });

  const createSetupIntentMutation = useMutationCreateSetupIntent({
    onErrorCallback: (errorMsg) => {
      setErrorOnAddCard(errorMsg);
    },
  });

  const addPaymentMethodMutation = useMutationAddPaymentMethod({
    shouldToast: false,
    onErrorCallback: (errorMsg) => {
      setErrorOnAddCard(errorMsg);
    },
  });

  const notifySuccess = () => {
    toast.success(
      lt('text.hooks.useAddCreditCard.success', 'Card added successfully'),
    );
    onSuccess?.();
  };

  // NEW: create a SetupIntent, confirm the card with it (Stripe attaches the PM
  // to the customer + handles 3DS), then persist via `add-payment-method`.
  const addCardWithSetupIntent = async (
    cardElement: StripeCardNumberElement,
  ) => {
    if (!stripe) return;

    const { clientSecret } = await createSetupIntentMutation.mutateAsync({});

    const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (error) {
      setErrorOnAddCard(
        lt(
          `stripeError.${error.code}`,
          error.message ?? lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK),
        ),
      );
      return;
    }

    const paymentMethodId =
      typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id;

    if (!paymentMethodId) {
      setErrorOnAddCard(lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK));
      return;
    }

    await addPaymentMethodMutation.mutateAsync(
      { paymentMethodId },
      { onSuccess: notifySuccess },
    );
  };

  // LEGACY: tokenize with createPaymentMethod, then upsert via
  // `update-payment-method`.
  const addCardLegacy = async (cardElement: StripeCardNumberElement) => {
    if (!stripe) return;

    const stripePayment = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (
      stripePayment.error &&
      stripePayment.error.code !== 'setup_intent_unexpected_state'
    ) {
      setErrorOnAddCard(
        lt(
          `stripeError.${stripePayment.error.code}`,
          stripePayment.error.message ?? '',
        ) || lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK),
      );
      return;
    }
    let paymentMethodId = null;

    if (
      stripePayment.error &&
      stripePayment.error.code === 'setup_intent_unexpected_state'
    ) {
      // this card already setup intent success
      paymentMethodId = stripePayment.error.setup_intent
        ?.payment_method as string;
    } else {
      paymentMethodId = stripePayment.paymentMethod?.id as string;
    }

    if (!paymentMethodId) return;

    const paymentInfo = {
      paymentMethod: {
        id: paymentMethodId,
        method: PAYMENT_METHOD.CREDIT_CARD,
      },
    };

    await savePaymentInfoMutation.mutateAsync(paymentInfo, {
      onSuccess: notifySuccess,
    });
  };

  const handleAddCard = async (
    event: React.SyntheticEvent<HTMLButtonElement>,
  ): Promise<void> => {
    setIsAddingCard(true);

    try {
      event.preventDefault();
      if (!stripe || !elements) {
        return;
      }

      const cardElement = elements.getElement('cardNumber');
      if (!cardElement) return;

      setErrorOnAddCard(null);

      if (isMultiCardsPerUserEnabled) {
        await addCardWithSetupIntent(cardElement);
      } else {
        await addCardLegacy(cardElement);
      }
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setIsAddingCard(false);
    }
  };

  return {
    errorOnAddCard,
    handleAddCard,
    isAddingCard,
  };
};
